/**
 * Design tokens — v13, "Wormhole Console."
 *
 * Correction pass. v12 read the brief's "LCARS influence" as a licence to
 * rebuild the whole chrome out of LCARS: elbows, capped rails and a
 * five-colour rail family carrying structure everywhere. The user's verdict:
 * *"the general UI UX is now too much LCARS... LESS LCARS MORE
 * ../adventure/frontend design."*
 *
 * So the base world is now the corp's own `adventure` app, copied properly
 * rather than paraphrased:
 *  - Its palette exactly: void black, wormhole purple as THE identity colour,
 *    scanner cyan as the one secondary, gold rare, cool near-white text.
 *  - Its shapes: soft 10px radius panels and full pills — not colour-blocked
 *    rectangles.
 *  - Its depth: glow, never a neutral drop shadow.
 *  - Its type: Orbitron for the display moments, Rajdhani for labels, Inter
 *    for prose, Share Tech Mono for real data.
 *  - Its signature idiom: the ScanFrame corner bracket (targeting reticle).
 *
 * LCARS survives exactly where `adventure` itself uses it — one small elbow
 * tab on a panel header (see its FloatingConsolePanel). That is what
 * "lightweight influence" meant. Elbows and rails are garnish here, never
 * the structure.
 */

export const C = {
  // ---- Ground. Adventure's own three-step stack, which the login screen
  // already reads — so the gate and the console are finally one world
  // instead of two palettes bolted together.
  bg: '#05040c',
  surface: '#0a0816',
  panel: '#100c22',
  panelAlt: '#100c22',

  // Aliases kept because ~30 files import these names. They now resolve into
  // the adventure stack rather than to v11's blue-grey slabs.
  void: '#05040c',
  panelFill: '#0a0816',
  cardFill: '#0a0816',

  // Borders are wormhole-tinted, never plain grey — adventure's own rule.
  hairline: 'rgba(151,113,255,0.16)',
  hairlineBright: 'rgba(151,113,255,0.42)',
  borderSub: 'rgba(255,255,255,0.06)',

  // ---- The identity pair. Two accents carry the whole interface: purple is
  // "the corp / the gate", cyan is "actively scanning or reading out". Never
  // mixed on one element (adventure's One Glow Rule).
  wormhole: '#9771ff',
  wormholeDim: 'rgba(151,113,255,0.16)',
  cyan: '#5ee7ff',

  // `blue` and `lilac` both resolve to the wormhole purple: every existing
  // call site inherits the identity colour instead of puncturing it.
  blue: '#9771ff',
  lilac: '#9771ff',

  // ---- Status. Data values only, never decoration.
  green: '#3ddc84',
  gold: '#f0b429',
  red: '#ef5350',

  // v12's rail family, demoted to plain status/category tints so the files
  // that reference them keep working. They are NOT a structural palette any
  // more — do not colour chrome with them.
  amber: '#f0b429',
  salmon: '#ef5350',
  ice: '#5ee7ff',
  mauve: '#b98cff',

  // Cool near-white, in the same hue family as the accent — not neutral grey.
  //
  // textDim has been raised twice on readability reports: 0.66 -> 0.82 -> 0.88.
  // Measured against the real panel ground (C.surface at 72% over the void,
  // = #090713) with frontend/preview/contrast.mjs:
  //     0.66 -> 7.17:1    0.82 -> 10.79:1    0.88 -> 12.40:1
  // So contrast was never the actual fault — 0.82 already cleared AA body
  // text four times over. When secondary text still reads badly, suspect the
  // FACE before the alpha: Share Tech Mono is a thin display mono that
  // punishes running words at small sizes, and it belongs on data values
  // only (DESIGN.md's Mono-Signal Rule). 0.88 is kept because it was asked
  // for and costs nothing, not because it fixed the reported problem.
  text: '#e8e3ff',
  textDim: 'rgba(232,227,255,0.88)',
  textBright: '#ffffff',
  textOnFill: '#05040c',

  neutral: '#6b7280',

  // Login-screen legacy, unchanged and still read by WormholeGate/WormholeVoid.
  metal: '#3c3f47',
  metalDark: '#1b1d23',
  metalLight: '#63676f',
  glassTint: '#0f1524',
  glassEdge: '#9fc9ff',
} as const;

export const FONT = {
  // Adventure's own voices. Orbitron is the "ship's computer" display face —
  // wide, geometric, uppercase, used for the handful of real moments. It is
  // never body text and never a data value.
  display: '"Orbitron", "Rajdhani", sans-serif',
  // Rajdhani is the everyday HUD voice: tracked-out uppercase for every
  // status label and control. Legible small, which Antonio is not.
  sub: '"Rajdhani", "Inter", sans-serif',
  body: 'Inter, system-ui, -apple-system, sans-serif',
  mono: '"Share Tech Mono", monospace', // real data values only
  // Antonio survives for ONE thing: the LCARS elbow tab on a panel header,
  // exactly as adventure uses it. Not a general display face.
  lcars: '"Antonio", "Rajdhani", sans-serif',
} as const;

export const RADIUS = 10; // adventure's soft-rounded default
export const PANEL_RADIUS = 10;
export const RAIL_RADIUS = 999; // real buttons and the nav capsule
export const ELBOW_RADIUS = 10; // the header tab's corner, matching RADIUS

export const GLASS_BLUR = 'blur(18px) saturate(160%)';

export const REDUCE_MOTION = '@media (prefers-reduced-motion: reduce)';

export const zIndex = {
  backdrop: 0,
  content: 1,
  navbar: 10,
} as const;

export const prefersReducedMotion = (): boolean =>
  typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches === true;

/**
 * Tint any palette value to an alpha.
 *
 * Not every colour in `C` is a hex string — `textDim` and `hairline` are
 * already `rgba(...)`, and `classColor()` returns `textDim` for anything it
 * cannot rank (k-space destinations like "nullsec", an unknown class). Those
 * used to fall through `parseInt` as NaN and produce `rgba(NaN,NaN,NaN,a)`,
 * which browsers drop entirely — the caller silently lost its border or fill
 * rather than getting a wrong colour, which is why it went unnoticed.
 */
export const hexToRgba = (color: string, alpha: number): string => {
  // Already a channel triplet: restate it at the requested alpha.
  const rgb = /^rgba?\(\s*([\d.]+)\s*,\s*([\d.]+)\s*,\s*([\d.]+)/.exec(color);
  if (rgb) return `rgba(${rgb[1]},${rgb[2]},${rgb[3]},${alpha})`;

  const hex = color.replace('#', '');
  // #abc as well as #aabbcc, since a 3-digit hex parses to a plausible-but
  // -wrong 24-bit number rather than failing.
  const full = hex.length === 3 ? hex.replace(/./g, (c) => c + c) : hex;
  if (!/^[0-9a-f]{6}$/i.test(full)) return color; // named colour, gradient, etc. — pass through unchanged
  const n = parseInt(full, 16);
  return `rgba(${(n >> 16) & 255},${(n >> 8) & 255},${n & 255},${alpha})`;
};

/** Glow is this world's depth vocabulary — adventure's No-Card-Shadow rule.
 * A colored halo in the wormhole or scanner hue, never a neutral shadow, and
 * never both hues on one element. */
export const glow = (hex: string, radius = 24, alpha = 0.45): string => `0 0 ${radius}px ${hexToRgba(hex, alpha)}`;

/** A real offset+blur shadow. Transient overlays only (menus, poppers, the
 * search dropdown) where a surface genuinely floats above the page. */
export function depthSx() {
  return { boxShadow: '0 10px 34px rgba(5,4,12,0.5), 0 2px 8px rgba(5,4,12,0.4)' } as const;
}

/**
 * The universal panel: a translucent surface over the void, a wormhole-tinted
 * hairline, soft 10px corners, and a glow that comes up on hover. No colored
 * top rail — that was v12's LCARS move and it is exactly what made every
 * screen read as a control panel from a different show.
 */
export function frameSx(accent: string = C.wormhole) {
  return {
    position: 'relative' as const,
    backgroundColor: hexToRgba(C.surface, 0.72),
    backdropFilter: 'blur(10px)',
    border: `1px solid ${C.hairline}`,
    borderRadius: `${RADIUS}px`,
    transition: 'border-color 0.25s ease, box-shadow 0.25s ease',
    '&:hover': { borderColor: hexToRgba(accent, 0.4), boxShadow: glow(accent, 22, 0.12) },
  } as const;
}

/** @deprecated Alias of `frameSx`, kept so existing call sites compile. */
export const igniteSx = frameSx;
/** @deprecated Alias of `frameSx`. */
export const glassPanelSx = frameSx;
/** @deprecated Alias of `frameSx`. */
export const glassSurfaceSx = frameSx;

/** The floating capsule treatment — adventure's nav bar and any surface that
 * should read as hovering over the void rather than sitting in the page. */
export function capsuleSx(accent: string = C.wormhole, lifted = false) {
  return {
    borderRadius: `${RAIL_RADIUS}px`,
    backgroundColor: hexToRgba(C.surface, 0.62),
    backdropFilter: GLASS_BLUR,
    border: `1px solid ${hexToRgba(accent, lifted ? 0.28 : 0.16)}`,
    boxShadow: lifted
      ? `0 10px 34px rgba(5,4,12,0.5), 0 0 0 1px ${hexToRgba(accent, 0.06)}`
      : '0 6px 24px rgba(5,4,12,0.32)',
  } as const;
}

/** The sliding indicator behind an active nav item: a soft pill of accent
 * light with an inset bloom — adventure's own treatment, not a glass slab. */
export function navPillSx(accent: string = C.cyan) {
  return {
    borderRadius: `${RAIL_RADIUS}px`,
    backgroundColor: hexToRgba(accent, 0.14),
    boxShadow: `0 0 12px ${hexToRgba(accent, 0.3)} inset, 0 0 10px ${hexToRgba(accent, 0.18)}`,
  } as const;
}

/**
 * The LCARS elbow tab — the ONE place LCARS is allowed to show, copied from
 * adventure's own FloatingConsolePanel: a small solid accent tab sitting on
 * top of a panel, its bottom corners square where it meets the body.
 */
export function elbowTabSx(accent: string) {
  return {
    display: 'inline-block',
    backgroundColor: accent,
    color: C.bg,
    fontFamily: FONT.lcars,
    fontWeight: 700,
    letterSpacing: '0.14em',
    textTransform: 'uppercase' as const,
    fontSize: '0.7rem',
    lineHeight: 1.6,
    px: 1.5,
    borderRadius: `${ELBOW_RADIUS}px ${ELBOW_RADIUS}px 0 0`,
  } as const;
}

/** A status/category mark: mono text in the accent, tinted hairline, pill. */
export function badgeSx(accent: string) {
  return {
    backgroundColor: hexToRgba(accent, 0.08),
    border: `1px solid ${hexToRgba(accent, 0.4)}`,
    color: accent,
    fontFamily: FONT.mono,
    borderRadius: `${RAIL_RADIUS}px`,
  } as const;
}

/** Small uppercase label — Rajdhani, tracked out. Adventure's
 * Uppercase-Means-Status rule: this voice always means a live readout. */
export function lcarsLabelSx(color: string = C.textDim, size = '0.78rem') {
  return {
    fontFamily: FONT.sub,
    fontWeight: 600,
    letterSpacing: '0.14em',
    textTransform: 'uppercase' as const,
    fontSize: size,
    lineHeight: 1.2,
    color,
  } as const;
}

/** A solid identity mark for the one thing a surface IS (a system's class,
 * a site's category). Budget: one or two per viewport. */
export function lcarsBlockSx(accent: string) {
  return {
    display: 'inline-flex',
    alignItems: 'center',
    backgroundColor: accent,
    color: C.bg,
    fontFamily: FONT.sub,
    fontWeight: 700,
    letterSpacing: '0.08em',
    textTransform: 'uppercase' as const,
    lineHeight: 1,
    borderRadius: `${RAIL_RADIUS}px`,
    px: 1.25,
    py: 0.5,
  } as const;
}

/** A divider: a hairline that fades out from the accent. One even line — no
 * asymmetric LCARS bar. */
export function lcarsRuleSx(accent: string = C.wormhole) {
  return {
    height: '1px',
    border: 'none',
    background: `linear-gradient(90deg, ${hexToRgba(accent, 0.45)} 0%, ${hexToRgba(accent, 0.1)} 60%, transparent 100%)`,
  } as const;
}

/** @deprecated No-op kept so existing call sites compile. */
export function panelWipeSx(_delayMs = 0) {
  return {} as const;
}

/** A small pulsing mark for a genuinely live state. Sparingly. */
export function glintDotSx(accent: string) {
  return {
    filter: `drop-shadow(0 0 4px ${hexToRgba(accent, 0.8)})`,
    flexShrink: 0,
    animation: prefersReducedMotion() ? 'none' : 'glintPulse 2.6s ease-in-out infinite',
    '@keyframes glintPulse': {
      '0%, 100%': { opacity: 1 },
      '50%': { opacity: 0.5 },
    },
  } as const;
}

/** Reveal-on-enter, from an already-visible default. */
export function revealSx(delayMs = 0) {
  return {
    animation: prefersReducedMotion() ? 'none' : `gobboReveal 0.45s cubic-bezier(0.16,1,0.3,1) ${delayMs}ms both`,
    '@keyframes gobboReveal': {
      from: { opacity: 0, transform: 'translateY(10px)' },
      to: { opacity: 1, transform: 'translateY(0)' },
    },
  } as const;
}

/** Danger-scaled color for a system class. Semantics unchanged. */
export function classColor(systemClass: string | null): string {
  if (!systemClass) return C.textDim;
  // C13 belongs with the special classes, not on the C1-C6 danger ramp: a
  // shattered frigate hole is not "worse than a C6", it is a different kind
  // of system, and the numeric branch below would otherwise paint it the same
  // red as capital-escalation space purely because 13 > 4.
  if (['Thera', 'C13', 'Drifter', 'Sentinel', 'Barbican', 'Vidette', 'Conflux', 'Redoubt'].includes(systemClass)) {
    return C.wormhole;
  }
  const n = Number(systemClass.replace('C', ''));
  if (!n) return C.textDim;
  if (n <= 3) return C.cyan;
  if (n === 4) return C.gold;
  return C.red;
}

/** Kept so v12 call sites compile. The world now runs on two accents, so
 * this alternates purple and cyan rather than walking five rail colours. */
export const RAIL_CYCLE = [C.wormhole, C.cyan] as const;
export const railFor = (index: number): string => RAIL_CYCLE[index % RAIL_CYCLE.length];

/** @deprecated v12 LCARS structure primitives. Retained only so nothing
 * breaks mid-migration; `elbowTabSx` is the one sanctioned LCARS shape. */
export function elbowSx(accent: string, _corner: 'tl' | 'tr' | 'bl' | 'br' = 'tl') {
  return { backgroundColor: accent, borderRadius: `${RADIUS}px`, flexShrink: 0 } as const;
}
export function railSx(accent: string, height = 10) {
  return { height, borderRadius: `${RAIL_RADIUS}px`, backgroundColor: accent, flexShrink: 0 } as const;
}
export function glassSx(accent: string) {
  return capsuleSx(accent, true);
}

/**
 * A scrollbar that belongs to this console rather than to the browser.
 *
 * Any panel that scrolls its own body is otherwise drawn half by us and half
 * by the platform — a grey Chrome bar down the side of a wormhole-tinted
 * panel, differently shaped again on every OS. This is the same hairline and
 * accent vocabulary the panels use, so a scrolling region reads as part of
 * the instrument.
 *
 * `scrollbarWidth`/`scrollbarColor` cover Firefox; the `::-webkit-` block
 * covers Chrome and Safari, which ignore the standard properties. Both are
 * needed — neither is a fallback for the other.
 */
export const thinScrollSx = {
  scrollbarWidth: 'thin' as const,
  scrollbarColor: `${hexToRgba(C.wormhole, 0.45)} transparent`,
  '&::-webkit-scrollbar': { width: 8, height: 8 },
  '&::-webkit-scrollbar-track': { background: 'transparent' },
  '&::-webkit-scrollbar-thumb': {
    backgroundColor: hexToRgba(C.wormhole, 0.35),
    borderRadius: 999,
    border: '2px solid transparent',
    backgroundClip: 'content-box',
  },
  '&::-webkit-scrollbar-thumb:hover': { backgroundColor: hexToRgba(C.wormhole, 0.6) },
} as const;
